import $ from 'jquery';
import Swal from 'sweetalert2';
import { getAuth0Client } from '~auth';
import { getUser } from '~src/user';
import {
  ParseSessionError,
  isOfcAccessEndedError,
} from '~auth/parseSessionError';
import { isOfcAccess } from '~auth/ofcAccess';
import {
  isAuth0SessionError,
  isBusinessRejection,
  showAccessErrorModal,
} from '~auth/sessionRecovery';
import { commonMiddleware } from './commonMiddleware';
import { router } from '../index';

// A concurrent route resolution must not open one error modal per caller.
let recoveryPromise;

const preserveIntendedRoute = () => {
  const lastResolvedRoutes = router.lastResolved();
  if (lastResolvedRoutes && lastResolvedRoutes[0]) {
    localStorage.setItem('authRedirect', JSON.stringify(lastResolvedRoutes[0]));
  }
};

const redirectToLogin = () => {
  preserveIntendedRoute();
  router.navigate(process.env.ROUTE_LOGIN);
};

const redirectToLogout = () => {
  router.navigate(process.env.ROUTE_LOGOUT);
};

const isRouteAuthenticated = async () => {
  // An Argo access (PE-2215) has no Auth0 session: the bootstrap below
  // validates its Parse session instead.
  if (isOfcAccess()) {
    return true;
  }

  try {
    const auth0 = await getAuth0Client();
    return await auth0.isAuthenticated();
  } catch (error) {
    // An unreadable Auth0 session is treated as "not authenticated": the login
    // route restarts the authorization, which is the only safe recovery.
    return false;
  }
};

const isRecoverableBootstrapError = (error) =>
  error instanceof ParseSessionError ||
  (!!error && typeof error === 'object' && typeof error.error === 'string');

const recoverBootstrapError = async (error) => {
  // The ended Argo access already shows «Sessione terminata»: no modal.
  if (isOfcAccessEndedError(error)) {
    return false;
  }

  const auth0Session = isAuth0SessionError(error);
  const business = isBusinessRejection(error);
  const result = await showAccessErrorModal({ business });

  if (result.dismiss === Swal.DismissReason.cancel) {
    // Explicit identity change: revocation is confirmed by the logout route.
    redirectToLogout();
    return false;
  }

  if (!result.isConfirmed) {
    return false;
  }

  if (business) {
    redirectToLogout();
    return false;
  }

  if (auth0Session) {
    // The Auth0 session cannot be refreshed, so retrying Parse is pointless:
    // restart the authorization while preserving the intended route.
    redirectToLogin();
    return false;
  }

  // Deliberate retry that keeps the stored Parse token and never falls back to
  // the legacy /startup bootstrap. The Auth0 callback is not involved here.
  try {
    await getUser({}, true);
    return true;
  } catch (retryError) {
    if (!isRecoverableBootstrapError(retryError)) throw retryError;
    return recoverBootstrapError(retryError);
  }
};

const bootstrapWithRecovery = async () => {
  try {
    await getUser();
    return true;
  } catch (error) {
    if (!isRecoverableBootstrapError(error)) throw error;
    return recoverBootstrapError(error);
  }
};

const bootstrapWithSharedRecovery = () => {
  if (!recoveryPromise) {
    recoveryPromise = bootstrapWithRecovery().finally(() => {
      recoveryPromise = null;
    });
  }

  return recoveryPromise;
};

// Shared auth/bootstrap boundary for every protected entry route. It returns
// `true` only when the route is allowed to render: the caller MUST stop when it
// gets `false` because Navigo does not await handlers and a bare `return` in a
// middleware would not abort the route.
export const runProtectedRoute = async (options) => {
  if (!(await isRouteAuthenticated())) {
    redirectToLogin();
    return false;
  }

  const ready = await bootstrapWithSharedRecovery();

  if (!ready) {
    // Stop the protected route rendering: no content, no loading spinner.
    $('.editor-route').hide();
    $('.app-spinner').hide();
    return false;
  }

  // The user context is now cached, so the middleware gates below reuse it
  // without another network round trip and keep their disabled/org/demo messages.
  return commonMiddleware(options);
};
